import { createRouter, createWebHistory } from 'vue-router'
import { vuexOidcCreateRouterMiddleware } from 'vuex-oidc'
import OidcCallback from '@/views/OidcCallback.vue'
import OidcError from '@/views/OidcError.vue'
import OidcSilentRenew from '@/views/OidcSilentRenew.vue'
import Home from '@/views/Home.vue'
import ReplacementTrainHistory from '@/views/ReplacementTrainHistory.vue'
import TrainHistory from '@/views/TrainHistory.vue'
import VehicleHistory from '@/views/VehicleHistory.vue'
import VehicleUtilizationHistory from '@/views/VehicleUtilizationHistory.vue'
import PunctualityDashboard from '@/views/PunctualityDashboard.vue'
import RouteHotspots from '@/views/RouteHotspots.vue'
import Connections from '@/views/Connections.vue'
import Admin from '@/views/Admin.vue'
import store from '@/store'

const routes = [
  {
    path: '/',
    name: 'home',
    component: Home,
  },
  {
    path: '/train/:trainNumber',
    name: 'train',
    component: TrainHistory,
    props: true,
  },
  {
    path: '/vehicle/:vehicle',
    name: 'vehicle',
    component: VehicleHistory,
    props: true,
  },
  {
    path: '/replacement-trains',
    name: 'replacement-trains',
    component: ReplacementTrainHistory,
    props: true,
    meta: {
      title: "Ersatzzüge",
      icon: "mdi-swap-horizontal",
      path: "/replacement-trains",
      hidden: true,
    },
  },
  {
    path: '/vehicle-utilization',
    name: 'vehicle-utilization',
    component: VehicleUtilizationHistory,
    props: true,
    meta: {
      title: "Fahrzeugeinsatz",
      icon: "mdi-chart-bar",
      path: "/vehicle-utilization",
      hidden: true,
    },
  },
  {
    path: '/verbindungen',
    name: 'connections',
    component: Connections,
    meta: {
      title: "Verbindungen",
      icon: "mdi-map-marker-path",
      path: "/verbindungen",
      // Marks the entry with a lock in the drawer. Deliberately NOT a redirect guard like
      // `requiresAdmin`: the page stays reachable so it can say why it is shut and offer the
      // sign-in that comes back to it. The real gate is server-side — `/api/connections` and
      // `/api/stations` are the only read endpoints that require a token.
      requiresAuth: true,
    },
  },
  {
    path: '/punctuality',
    name: 'punctuality',
    component: PunctualityDashboard,
    props: true,
    meta: {
      title: "Pünktlichkeit",
      icon: "mdi-clock-check-outline",
      path: "/punctuality",
    },
  },
  {
    path: '/hotspots',
    name: 'hotspots',
    component: RouteHotspots,
    props: true,
    meta: {
      title: "Hotspots",
      icon: "mdi-map-marker-alert",
      path: "/hotspots",
    },
  },
  {
    path: '/admin',
    name: 'admin',
    component: Admin,
    props: true,
    meta: {
      title: "Admin",
      icon: "mdi-shield-account",
      path: "/admin",
      requiresAdmin: true,
    },
  },
  {
    path: '/notifications',
    name: 'notifications',
    component: () => import('../views/Notifications.vue'),
    meta: {
      title: "Benachrichtigungen",
      icon: "mdi-bell-outline",
      path: "/notifications",
      // Listed in the drawer only while this device has subscriptions (App.vue);
      // the page itself stays reachable.
      requiresNotifications: true,
    },
  },
  {
    path: '/mcp',
    name: 'mcp',
    component: () => import('../views/McpGuide.vue'),
    meta: {
      title: "MCP",
      icon: "mdi-robot-outline",
      path: "/mcp",
    },
  },
  {
    path: '/about',
    name: 'about',
    // route level code-splitting
    // this generates a separate chunk (about.[hash].js) for this route
    // which is lazy-loaded when the route is visited.
    component: () => import('../views/About.vue'),
    meta: {
      title: "Info",
      icon: "mdi-information-outline",
      path: "/about",
    },
  },
  {
    path: '/impressum',
    name: 'impressum',
    component: () => import('../views/Impressum.vue'),
    meta: {
      title: "Impressum",
      icon: "mdi-script-text-outline",
      path: "/impressum",
      isPublic: true,
    },
  },
  {
    path: '/oidc/callback',
    name: 'oidcCallback',
    component: OidcCallback
  },
  {
    path: '/oidc/silent-renew',
    name: 'oidcSilentRenew',
    component: OidcSilentRenew
  },
  {
    path: '/oidc/error',
    name: 'oidcError',
    component: OidcError,
    meta: {
      isPublic: true,
    },
  },
]

const router = createRouter({
  history: createWebHistory(),
  routes: routes,
})
router.beforeEach(vuexOidcCreateRouterMiddleware(store, 'oidcStore'))
router.beforeEach((to, from, next) => {
  if (to.meta && to.meta.requiresAdmin && !store.state.admin) {
    next('/')
  } else {
    next()
  }
})

// One hook rather than a hook per view: every way into a detail page goes
// through the router — the search, a favourite, the hero chip, a TrainLink or
// VehicleLink inside a trip row, or a URL someone pasted.
router.afterEach((to, from, failure) => {
  if (failure) {
    return
  }
  if (to.name === 'train' && to.params.trainNumber) {
    store.commit('recordVisit', { type: 'train', id: String(to.params.trainNumber) })
  } else if (to.name === 'vehicle' && to.params.vehicle) {
    store.commit('recordVisit', { type: 'vehicle', id: String(to.params.vehicle) })
  }
})

export default router
